/* ============================================================================
   MADE → BAG — la creación entra en la bolsa
   ----------------------------------------------------------------------------
   El segundo momento firma. El primero convierte un diseño en producto; éste
   se lo lleva.

   LA REGLA QUE LO SOSTIENE: lo que vuela lleva EL DISEÑO DEL CLIENTE. No una
   camiseta en blanco, no la foto de catálogo, no un icono. Si volara un
   producto genérico, el momento no diría nada — y esa es exactamente la
   diferencia entre « se ha añadido algo » y « MI creación está en MI bolsa ».

   DE DÓNDE SALE ESA IMAGEN, por orden y con degradación (§18):
     1. un recorte transparente, si algún día existe
     2. el aperçu personalizado que el Studio ya sabe generar EN LOCAL
     3. como último recurso, una ficha con el nombre — nunca el producto vacío

   El aperçu local es la clave: existe antes de subir nada a Cloudinary, así
   que el vuelo empieza en cuanto se pulsa y no espera a la red. La subida
   sigue su curso por detrás.

   Y LO QUE NO HACE: la animación NO confirma nada. Sólo `/cart/add.js`
   confirma. El icono reacciona y el contador cambia cuando la tienda dice que
   sí; si dice que no, la pieza vuelve y el diseño sigue intacto.

   Sin librerías: WAAPI y DOM, como el resto de la casa.
   ========================================================================== */

const VOL_PANIER = (() => {
'use strict';

const $ = (s, r = document) => r.querySelector(s);

const REDUCIDO = () =>
  window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const ms = (nombre, porDefecto) => {
  const v = getComputedStyle(document.documentElement).getPropertyValue(nombre).trim();
  if (!v) return porDefecto;
  return v.endsWith('ms') ? parseFloat(v) : parseFloat(v) * 1000;
};

/** Salida suave: sale rápido y se posa. */
const suavizar = (t) => 1 - (1 - t) ** 3;

/**
 * La curva. Una recta se lee como un deslizamiento; un arco corto se lee como
 * un objeto que sale de una escena y entra en otra. Bézier cuadrática con
 * rects REALES: si la barra cambia de sitio, la curva cambia sola.
 */
function trayectoria(a, b, { pasos = 26, alzada = 46, escalaFinal = 0.2 } = {}) {
  const x0 = a.left + a.width / 2, y0 = a.top + a.height / 2;
  const x1 = b.left + b.width / 2, y1 = b.top + b.height / 2;
  const cx = x0 + (x1 - x0) * 0.6;
  const cy = Math.min(y0, y1) - alzada;

  const marcos = [];
  for (let i = 0; i <= pasos; i++) {
    const t = i / pasos, u = 1 - t;
    const x = u * u * x0 + 2 * u * t * cx + t * t * x1;
    const y = u * u * y0 + 2 * u * t * cy + t * t * y1;
    const escala = 1 + (escalaFinal - 1) * suavizar(t);
    const giro = (-2.5 * Math.sin(Math.PI * t)).toFixed(2);
    const opacidad = t < 0.84 ? 1 : 1 - ((t - 0.84) / 0.16) * 0.8;
    marcos.push({
      offset: t,
      transform: `translate(${(x - x0).toFixed(1)}px, ${(y - y0).toFixed(1)}px)`
        + ` scale(${escala.toFixed(4)}) rotate(${giro}deg)`,
      opacity: opacidad.toFixed(3),
    });
  }
  return marcos;
}

/** La pieza que viaja: la creación del cliente dentro de una caja. */
function pieza(imagen, rect) {
  const caja = document.createElement('div');
  caja.className = 'vol-piece';
  caja.setAttribute('aria-hidden', 'true');
  Object.assign(caja.style, {
    position: 'fixed',
    left: `${rect.left}px`, top: `${rect.top}px`,
    width: `${rect.width}px`, height: `${rect.height}px`,
    zIndex: '9999', pointerEvents: 'none', willChange: 'transform, opacity',
  });
  if (imagen) {
    const img = document.createElement('img');
    img.src = imagen;
    img.alt = '';
    img.decoding = 'async';
    caja.append(img);
  } else {
    // Último recurso: una ficha, nunca el producto vacío.
    caja.dataset.ficha = 'true';
  }
  return caja;
}

/**
 * El vuelo.
 *
 * @param {object} o
 *   origen    Element  el escenario del producto terminado
 *   imagen    string   dataURL o URL de la creación
 *   destino   DOMRect  dónde está la bolsa AHORA (se resuelve al vuelo)
 * @returns {Promise<boolean>} resuelve al aterrizar
 */
function volar({ origen, imagen, destino }) {
  if (!origen || !destino || REDUCIDO()) return Promise.resolve(false);

  const a = origen.getBoundingClientRect();
  if (!a.width || !destino.width) return Promise.resolve(false);

  const clon = pieza(imagen, a);
  document.body.append(clon);

  /* El escenario se retira un paso mientras la pieza se va: es lo que
     convierte el vuelo en « esto ha salido de ahí ». El escenario REAL no se
     mueve ni se destruye — se marca, y el CSS hace el resto. */
  /* La marca va en <body>, no en el origen: el escenario real es `.plateau`
     y su `transform` lo escribe el zoom EN LÍNEA, así que una regla sobre él
     no se aplicaría nunca. Desde body, el CSS alcanza `.scene`. */
  document.body.dataset.volSuelta = 'true';

  const estrecho = window.matchMedia
    && window.matchMedia('(max-width: 767px)').matches;
  const duracion = Math.round(ms('--motion-fly', 560) * (estrecho ? 0.78 : 1));

  const anim = clon.animate(
    trayectoria(a, destino, {
      alzada: estrecho ? 16 : 46,
      escalaFinal: Math.max(0.07, Math.min((destino.width / a.width) * 1.8, 0.26)),
    }),
    { duration: duracion, easing: 'linear', fill: 'forwards' }
  );

  let limpio = false;
  const aterrizar = () => {
    if (limpio) return true;
    limpio = true;
    clon.remove();
    delete document.body.dataset.volSuelta;
    return true;
  };

  /* `anim.finished` no resuelve con la pestaña oculta: el navegador para el
     ciclo de fotogramas. Sin el temporizador, quien cambia de pestaña a mitad
     vuelve y se encuentra una miniatura flotando. */
  return Promise.race([
    anim.finished.catch(() => {}).then(aterrizar),
    new Promise((r) => setTimeout(() => r(aterrizar()), duracion + 260)),
  ]);
}

/**
 * El fallo. La creación NO desaparece: la pieza se disuelve donde está y el
 * escenario vuelve entero. Perder de vista el diseño justo cuando algo ha
 * fallado es la peor lectura posible.
 */
function deshacer() {
  delete document.body.dataset.volSuelta;
  document.querySelectorAll('.vol-piece').forEach((n) => {
    n.animate([{ opacity: getComputedStyle(n).opacity }, { opacity: 0 }],
      { duration: ms('--motion-panel', 400), fill: 'forwards' })
      .finished.catch(() => {}).then(() => n.remove());
  });
}

/* ------------------------------------------------- la superficie -------- */

/**
 * Lo que aparece cuando la creación ha aterrizado.
 *
 * No es un « toast ». Es la confirmación de que ESA pieza —la misma que
 * acaba de volar— está en la bolsa, con su nombre, su variante y el precio
 * que manda Shopify. El precio NO se calcula aquí: llega ya resuelto o no se
 * enseña.
 */
/* `urlPanier: null` quita el enlace en vez de dejarlo apuntando a /cart.
   Fuera de la tienda esa ruta NO EXISTE: Franklin pulso « Voir le panier »
   desde su Mac y se comio un 404 con tres piezas dentro. Un boton principal
   que lleva a una pagina de error es peor que no tener boton. */
function surface({ imagen, nom, variante, resume, prix, urlPanier = '/cart',
                  surSuivant = null }) {
  fermerSurface();

  const caja = document.createElement('div');
  caja.className = 'sacsurf';
  caja.setAttribute('role', 'dialog');
  caja.setAttribute('aria-modal', 'false');
  caja.setAttribute('aria-label', 'Panier');

  const linea = [variante, resume].filter(Boolean).join(' · ');

  caja.innerHTML = `
    <div class="sacsurf__piece">${imagen ? `<img src="${imagen}" alt="">` : ''}</div>
    <div class="sacsurf__corps">
      <p class="sacsurf__titre">Ajouté au panier</p>
      <p class="sacsurf__nom">${nom || ''}</p>
      ${linea ? `<p class="sacsurf__meta">${linea}</p>` : ''}
      ${prix ? `<p class="sacsurf__prix">${prix}</p>` : ''}
    </div>
    <div class="sacsurf__actions">
      ${urlPanier
        ? `<a class="btn btn--or sacsurf__voir" href="${urlPanier}">Voir le panier</a>`
        : `<span class="sacsurf__hors">Hors boutique : pas de panier ici</span>`}
      <button type="button" class="btn sacsurf__continuer">${
        surSuivant ? 'Personnaliser le suivant' : 'Continuer à créer'}</button>
    </div>
    <button type="button" class="sacsurf__fermer" aria-label="Fermer">&times;</button>`;

  caja.querySelector('.sacsurf__fermer').addEventListener('click', fermerSurface);
  /* « Continuer à créer » solo cerraba esta ventana y dejaba en el lienzo la
     pieza que se acababa de meter en la cesta. Para encadenar cuatro llaveros
     —que es lo que Franklin hace— habia que borrarlos a mano uno a uno. Con
     `surSuivant` el boton ademas limpia; sin el se comporta como siempre. */
  caja.querySelector('.sacsurf__continuer').addEventListener('click', () => {
    fermerSurface();
    if (surSuivant) { try { surSuivant(); } catch (e) { console.warn('[EDISAMP]', e); } }
  });
  document.body.append(caja);

  // Un fotograma con el estado cerrado calculado, o no hay recorrido que animar.
  getComputedStyle(caja).opacity;
  caja.dataset.ouvert = 'true';

  document.addEventListener('keydown', _escape);
  return caja;
}

function _escape(e) { if (e.key === 'Escape') fermerSurface(); }

function fermerSurface() {
  document.removeEventListener('keydown', _escape);
  const c = $('.sacsurf');
  if (!c) return;
  c.dataset.ouvert = 'false';
  setTimeout(() => c.remove(), ms('--motion-panel', 400));
}

/** El aviso de que no se ha podido. La creación sigue guardada, y se dice. */
function echec(mensaje) {
  fermerSurface();
  const caja = document.createElement('div');
  caja.className = 'sacsurf sacsurf--echec';
  caja.setAttribute('role', 'alert');
  caja.innerHTML = `
    <div class="sacsurf__corps">
      <p class="sacsurf__titre">Impossible d'ajouter au panier</p>
      <p class="sacsurf__meta">${mensaje || ''}</p>
      <p class="sacsurf__meta">Votre création est enregistrée.</p>
    </div>
    <button type="button" class="sacsurf__fermer" aria-label="Fermer">&times;</button>`;
  caja.querySelector('.sacsurf__fermer').addEventListener('click', fermerSurface);
  document.body.append(caja);
  getComputedStyle(caja).opacity;
  caja.dataset.ouvert = 'true';
  document.addEventListener('keydown', _escape);
  return caja;
}

return { volar, deshacer, trayectoria, surface, fermerSurface, echec, REDUCIDO };
})();

if (typeof window !== 'undefined') window.VOL_PANIER = VOL_PANIER;
